import { Product_TallyDimension } from "gen/product/models_pb"
import { TallyRow } from "gen/txn/models_pb"
import { Plus, Trash, X } from "lucide-react"
import { Decimal } from "shared-ts/classes/decimal"
import { MAX_DECIMAL_QUANTITY_PLACES } from "shared-ts/utils/constants"
import { Button } from "shared-web/components/base/control/Button"
import { IconButton } from "shared-web/components/base/control/IconButton"
import {
  QuantityInput,
  QuantityInputVariants,
} from "shared-web/components/base/input/QuantityInput"
import { Icon, IconSizes } from "shared-web/components/Icon"

import { FormErrors } from "hooks/useForm"

import {
  emptyTallyRow,
  formatTallyRows,
  tallyHasWidth,
  tallyRowTotal,
  tallyTotal,
} from "./tally.helpers"
import { TallyFormData } from "./tally.types"

interface TallySectionProps {
  tallyRows: TallyRow[]
  dimension: Product_TallyDimension
  onChangeRows: (rows: TallyRow[]) => void
  lengthDecimalPlaces: number
  tallyEntryDenominator: number
  uomCode: string
  errors: FormErrors<TallyFormData>
  readOnly: boolean
  allowNegativeQuantity?: boolean
  totalSuffix?: string
}

export function TallySection({
  tallyRows,
  dimension,
  onChangeRows,
  lengthDecimalPlaces,
  tallyEntryDenominator: denominator,
  uomCode,
  errors,
  readOnly,
  allowNegativeQuantity,
  totalSuffix,
}: TallySectionProps) {
  const unit = uomCode ? ` ${uomCode}` : ""
  const hasWidth = tallyHasWidth(dimension)
  const showMixedNumberInputs = denominator > 0
  const measurementUnit =
    dimension !== Product_TallyDimension.AREA ? uomCode : ""
  const widthOperator =
    dimension === Product_TallyDimension.UNIFIED_LENGTH ? Plus : X

  if (readOnly) {
    return (
      <div className="flex flex-col gap-y-1">
        {tallyRows.map((row, index) => (
          <div
            key={index}
            className="text-v2-foreground-default text-body flex items-center justify-between"
          >
            <span>
              {formatTallyRows([row], dimension, uomCode, denominator)}
            </span>
            <span>
              {tallyRowTotal(row, dimension)
                .round(MAX_DECIMAL_QUANTITY_PLACES)
                .toString()}
              {unit}
            </span>
          </div>
        ))}
        <div className="border-v2-border-default mt-1 flex justify-end border-t pt-2">
          <span className="text-v2-foreground-default text-body-lg-strong">
            Total: {tallyTotal(tallyRows, dimension).toString()}
            {unit}
            {totalSuffix && (
              <span className="text-v2-foreground-default-weak text-body ml-2">
                ({totalSuffix})
              </span>
            )}
          </span>
        </div>
      </div>
    )
  }

  const gridClassName = showMixedNumberInputs
    ? hasWidth
      ? "grid grid-cols-[6.5rem_1.5rem_12.5rem_1.5rem_12.5rem_minmax(4.5rem,1fr)_1.75rem] items-center gap-x-1.5"
      : "grid grid-cols-[6.5rem_1.5rem_12.5rem_minmax(4.5rem,1fr)_1.75rem] items-center gap-x-1.5"
    : hasWidth
      ? "grid grid-cols-[6.5rem_1.5rem_6.5rem_1.5rem_6.5rem_minmax(4.5rem,1fr)_1.75rem] items-center gap-x-1.5"
      : "grid grid-cols-[6.5rem_1.5rem_6.5rem_minmax(4.5rem,1fr)_1.75rem] items-center gap-x-1.5"

  const updateRow = (index: number, row: TallyRow) =>
    onChangeRows(tallyRows.map((r, i) => (i === index ? row : r)))

  return (
    <div className="flex flex-col gap-y-3">
      <div
        className={`text-v2-foreground-default-weak text-body-sm ${gridClassName}`}
      >
        <span className="text-body">Pieces</span>
        <span />
        <TallyMeasurementHeader
          label="Length"
          unit={measurementUnit}
          denominator={denominator}
        />
        {hasWidth && (
          <>
            <span />
            <TallyMeasurementHeader
              label="Width"
              unit={measurementUnit}
              denominator={denominator}
            />
          </>
        )}
        <span className="text-body text-right">Total</span>
        <span />
      </div>
      {tallyRows.map((row, index) => {
        const quantityError = errors[`tallyRows.${index}.quantity`]
        const lengthError = errors[`tallyRows.${index}.length`]
        const widthError = errors[`tallyRows.${index}.width`]
        return (
          <div key={index} className="flex flex-col gap-y-1">
            <div className={gridClassName}>
              <QuantityInput
                variant={QuantityInputVariants.SEGMENTED}
                width="fill"
                data-testid={`tally-quantity-${index}`}
                quantity={Decimal.fromPBOrZero(row.quantity)}
                onChangeQuantity={(quantity) =>
                  updateRow(index, { ...row, quantity })
                }
                min={allowNegativeQuantity ? undefined : Decimal.zero()}
                numDecimalPlaces={0}
                invalid={!!quantityError}
              />
              <span className="flex justify-center">
                <Icon
                  className="text-v2-foreground-default-weak"
                  icon={X}
                  size={IconSizes.ExtraSmall}
                />
              </span>
              <TallyMeasurementInput
                testId={`tally-length-${index}`}
                value={Decimal.fromPBOrZero(row.length)}
                onChange={(length) => updateRow(index, { ...row, length })}
                invalid={!!lengthError}
                denominator={denominator}
                decimalPlaces={lengthDecimalPlaces}
              />
              {hasWidth && (
                <>
                  <span className="flex justify-center">
                    <Icon
                      className="text-v2-foreground-default-weak"
                      icon={widthOperator}
                      size={IconSizes.ExtraSmall}
                    />
                  </span>
                  <TallyMeasurementInput
                    testId={`tally-width-${index}`}
                    value={Decimal.fromPBOrZero(row.width)}
                    onChange={(width) => updateRow(index, { ...row, width })}
                    invalid={!!widthError}
                    denominator={denominator}
                    decimalPlaces={lengthDecimalPlaces}
                  />
                </>
              )}
              <span className="text-v2-foreground-default text-body whitespace-nowrap text-right">
                {tallyRowTotal(row, dimension)
                  .round(MAX_DECIMAL_QUANTITY_PLACES)
                  .toString()}
                {unit}
              </span>
              <IconButton
                icon={Trash}
                onClick={() =>
                  onChangeRows(tallyRows.filter((_, i) => i !== index))
                }
              />
            </div>
            {(quantityError || lengthError || widthError) && (
              <span className="text-v2-foreground-danger text-body-sm">
                {quantityError ?? lengthError ?? widthError}
              </span>
            )}
          </div>
        )
      })}
      <Button
        mode="secondary"
        className="self-start"
        onClick={() => onChangeRows([...tallyRows, emptyTallyRow(dimension)])}
        leftIcon={Plus}
      >
        Add row
      </Button>
      <div className="border-v2-border-default flex justify-end border-t pt-2">
        <span className="text-v2-foreground-default text-body-lg-strong">
          Total: {tallyTotal(tallyRows, dimension).toString()}
          {unit}
        </span>
      </div>
      {errors.tallyRows && (
        <span className="text-v2-foreground-danger text-body-sm">
          {errors.tallyRows}
        </span>
      )}
    </div>
  )
}

function TallyMeasurementHeader({
  label,
  unit,
  denominator,
}: {
  label: string
  unit: string
  denominator: number
}) {
  const title = unit ? `${label} (${unit})` : label
  if (denominator === 0) {
    return <span className="text-body">{title}</span>
  }
  return (
    <span className="text-body flex gap-x-1">
      <span className="flex-1">{title}</span>
      <span className="flex-1">/{denominator}</span>
    </span>
  )
}

function TallyMeasurementInput({
  testId,
  value,
  onChange,
  invalid,
  denominator,
  decimalPlaces,
}: {
  testId: string
  value: Decimal
  onChange: (value: Decimal) => void
  invalid: boolean
  denominator: number
  decimalPlaces: number
}) {
  if (denominator === 0) {
    return (
      <QuantityInput
        variant={QuantityInputVariants.SEGMENTED}
        width="fill"
        data-testid={testId}
        quantity={value}
        onChangeQuantity={onChange}
        min={Decimal.zero()}
        numDecimalPlaces={decimalPlaces}
        invalid={invalid}
      />
    )
  }
  // A value that doesn't land on a whole part keeps its remainder as a decimal
  // numerator, rounded to quantity precision.
  const { whole, numerator } = value.splitMixedNumber(
    denominator,
    MAX_DECIMAL_QUANTITY_PLACES
  )
  const change = (nextWhole: number, nextNumerator: Decimal) =>
    onChange(
      Decimal.fromBigSource(nextWhole).add(
        nextNumerator.div(Decimal.fromBigSource(denominator))
      )
    )
  return (
    <div className="flex gap-x-1">
      <QuantityInput
        variant={QuantityInputVariants.SEGMENTED}
        width="fill"
        data-testid={`${testId}-whole`}
        quantity={Decimal.fromBigSource(whole)}
        onChangeQuantity={(next) => change(Number(next.int()), numerator)}
        min={Decimal.zero()}
        numDecimalPlaces={0}
        invalid={invalid}
      />
      <QuantityInput
        variant={QuantityInputVariants.SEGMENTED}
        width="fill"
        data-testid={`${testId}-numerator`}
        quantity={numerator}
        onChangeQuantity={(next) => change(whole, next)}
        min={Decimal.zero()}
        max={Decimal.fromBigSource(
          denominator - 10 ** -MAX_DECIMAL_QUANTITY_PLACES
        )}
        incrementMax={Decimal.fromBigSource(denominator - 1)}
        numDecimalPlaces={MAX_DECIMAL_QUANTITY_PLACES}
        invalid={invalid}
      />
    </div>
  )
}
